import { map } from 'rxjs/operators';
import { SCATTER_PLOT_STATE_KEYS } from 'ChartApp/ts/apps/scatter-plot/state/constants';
import { AjaxService } from 'NewAngular/services/core/ajax.service';
import { UrlsService } from 'NewAngular/services/core/urls.service';
import { convertNestedStringToBoolean } from 'NewAngular/utils/state-utils';
import { formFlattenedObject, formFlattenedParamString } from 'NewAngular/utils/urls';
import { TechChartUrlSerializer } from 'ChartApp/ts/features/tech-chart-page-shared/state/tech-chart.serializer';
import * as i0 from "@angular/core";
import * as i1 from "NewAngular/services/core/ajax.service";
import * as i2 from "NewAngular/services/core/urls.service";
export class ScatterPlotService {
    constructor(ajaxService, urlsService) {
        this.ajaxService = ajaxService;
        this.urlsService = urlsService;
        this.SCATTER_PLOT_PARAMS_REQUIRED = ['xAxisMetric', 'yAxisMetric'];
        this.serializedKeys = [];
        this.serializedKeys = SCATTER_PLOT_STATE_KEYS;
    }
    /**
     * Get a flattened object of the scatter plot state.
     *
     * @param scatterPlotState {ScatterPlotStore}
     */
    getFlattenedFormData(scatterPlotState) {
        return formFlattenedObject(scatterPlotState, this.serializedKeys, TechChartUrlSerializer);
    }
    /**
     * Check that the params exist and, depending on the plot type, have the required properties to make a backend
     * request without errors.
     * @param params - The scatter plot get params.
     * @returns Boolean describing whether all required params exist.
     * @private
     */
    hasValidFetchParams(params) {
        if (params === null || !params.securities)
            return false;
        for (let i = 0; i < this.SCATTER_PLOT_PARAMS_REQUIRED.length; i += 1) {
            if (!params[this.SCATTER_PLOT_PARAMS_REQUIRED[i]])
                return false;
        }
        return true;
    }
    /** Get json data for both types of scatter plots (point in time, timeseries) from ScatterPlotDataView.
     *  For a saved chart, specify the chart_id in params. */
    getScatterPlotData(params) {
        if (!this.hasValidFetchParams(params))
            return null;
        const urlParams = { format: 'json' };
        const url = this.urlsService.getUrl(this.urlsService.SCATTER_PLOT.SCATTER_PLOT_DATA, urlParams);
        return this.ajaxService.postFormEncodedDataObserveBody(url, params).pipe(map((response) => {
            convertNestedStringToBoolean(response);
            return response;
        }));
    }
    /**
     * Get image source url for making a request to the chart generator service.
     *
     * @param params {ScatterPlotDataParams}
     */
    getChartEmbedUrl(_params) {
        const params = { ..._params };
        const urlExtension = this.urlsService
            .getUrl(this.urlsService.EMBEDS.SCATTER_PLOT_CHART, undefined, params)
            .replace(/#/g, '%23');
        return `${process.env.NODE_BASE_URL}${urlExtension}`;
    }
    /**
     * Get share url that will authenticate with Twitter and redirect to allow sharing a Scatter Plot.
     *
     * @param baseShareUrl {string}
     * @param scatterPlotState {ScatterPlotStore}
     */
    getScatterPlotShareUrl(baseShareUrl, scatterPlotState) {
        const data = this.getFlattenedFormData(scatterPlotState);
        return this.ajaxService.postFormEncodedDataObserveBody(baseShareUrl, data);
    }
    /**
     * Get the url to the chart image saved in the S3 bucket.
     *
     * @param params {ScatterPlotDataParams}
     */
    getChartSaveAsImageUrl(params) {
        const baseUrl = this.urlsService.EMBEDS.SAVE_AS_IMAGE_SCATTER_PLOT_CHART;
        return this.ajaxService.postFormEncodedDataObserveResponse(baseUrl, params);
    }
    /**
     * Get disclosures about metrics and securities included in the chart.
     *
     * @param params {ScatterPlotDataParams}
     */
    getChartDisclosure(params) {
        const url = this.urlsService.DISCLOSURES.SCATTER_PLOT_DISCLOSURE;
        return this.ajaxService.postFormEncodedDataWithTextObserveResponse(url, params);
    }
    /**
     * Get scatter plot data in csv format.
     *
     * @param params
     */
    getChartCSVDataFromState(params) {
        const urlParams = { format: 'csv' };
        const url = this.urlsService.getUrl(this.urlsService.SCATTER_PLOT.SCATTER_PLOT_DATA, urlParams);
        return this.ajaxService.postFormEncodedDataWithBlobObserveResponse(url, params);
    }
    getChartDataUrlQuery(scatterPlotState) {
        // Add 'redesign' true for Node
        const state = { ...scatterPlotState, redesign: true };
        const serializedKeys = SCATTER_PLOT_STATE_KEYS.concat(['redesign', 'chartAnnotations']);
        return formFlattenedParamString(state, serializedKeys, true);
    }
    getChartAppUrl(scatterPlotState) {
        return `${this.urlsService.SCATTER_PLOT.SCATTER_PLOT_PAGE}#/?${this.getChartDataUrlQuery(scatterPlotState)}`;
    }
}
ScatterPlotService.ɵfac = function ScatterPlotService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ScatterPlotService)(i0.ɵɵinject(i1.AjaxService), i0.ɵɵinject(i2.UrlsService)); };
ScatterPlotService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ScatterPlotService, factory: ScatterPlotService.ɵfac, providedIn: 'root' });
